import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";
import type PrescriptionJournalEvent from "./prescription-journal-event";
import type MedicationRequest from "./prescriptions/medication-request";

//---- Automatically generated, don't update this part ----

type ValidityPeriodStringEnum =
  | "1-day"
  | "2-days"
  | "3-days"
  | "4-days"
  | "5-days"
  | "6-days"
  | "7-days"
  | "8-days"
  | "9-days"
  | "10-days"
  | "11-days"
  | "12-days"
  | "13-days"
  | "1-week"
  | "2-weeks"
  | "3-weeks"
  | "1-month"
  | "2-months"
  | "3-months"
  | "4-months"
  | "5-months"
  | "6-months"
  | "1-year";

/**
 * An event created when a Medic prescibes drugs to a Patient.
 * It contains a list of medication, and a custom field letting the
 * Medic enter additional medication manually.
 */
export default class MedicationPrescribedJournalEvent
  extends JournalEvent
  implements PrescriptionJournalEvent
{
  static type = "medication-prescribed-journal-event";

  /**
   * Validity period of the prescription, [duration-unit]
   */
  @attr("string") declare validityPeriod: ValidityPeriodStringEnum;

  /**
   * A list of prescribed medication
   */
  @attr("medication-requests", {
    defaultValue() {
      return [];
    }
  })
  declare medications: MedicationRequest[];

  /**
   * Additional drugs that can be prescribed in ALD
   */
  @attr("string") declare otherMedicationsAld: string;

  /**
   * Additional drugs that can be prescribed
   */
  @attr("string") declare otherMedications: string;

  /**
   * Identity of the prescriptor if different than the creator of the prescription.
   * A key-value object representing the prescriptor identity.
   *
   * For instance:
   *
   * ```js
   * {
   *   "first-name": "Jane",
   *   "last-name": "Doe",
   *   "professional-id": "123456"
   * }
   * ```
   */
  @attr() declare prescriptorIdentity: any;

  /**
   * The rules should create an alert or not when the prescription expires soon
   */
  @attr("boolean", {
    allowNull: true
  })
  declare createExpirationAlert: boolean;

  static validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.medication-prescribed-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      validityPeriod: validateInclusion({
        list: [
          "1-day",
          "2-days",
          "3-days",
          "4-days",
          "5-days",
          "6-days",
          "7-days",
          "8-days",
          "9-days",
          "10-days",
          "11-days",
          "12-days",
          "13-days",
          "1-week",
          "2-weeks",
          "3-weeks",
          "1-month",
          "2-months",
          "3-months",
          "4-months",
          "5-months",
          "6-months",
          "1-year"
        ],
        allowBlank: true,
        description: intl.t(
          "validations.medication-prescribed-journal-event.validity-period"
        )
      })
    };
  }

  //---- End of generated code ----
  @belongsTo("patient", {
    async: false,
    inverse: "medicationPrescribedJournalEvents"
  })
  declare patient: Patient;

  /**
   * @type {string}
   */
  @attr("string")
  declare uuid: string;

  get startDate(): Date {
    return this.occurredAt;
  }

  get endDate(): Date | undefined {
    const endDate = new Date(this.startDate);
    if (!this.validityPeriod) {
      return new Date(endDate.setFullYear(endDate.getFullYear() + 1));
    }
    const validityPeriodDuration = Number.parseInt(
      this.validityPeriod.split("-")[0]
    );
    const validityPeriodUnit = this.validityPeriod.split("-")[1];

    if (validityPeriodUnit?.includes("day")) {
      return new Date(
        endDate.setDate(endDate.getDate() + validityPeriodDuration)
      );
    } else if (validityPeriodUnit?.includes("week")) {
      return new Date(
        endDate.setDate(endDate.getDate() + validityPeriodDuration * 7)
      );
    } else if (validityPeriodUnit?.includes("year")) {
      return new Date(
        endDate.setFullYear(endDate.getFullYear() + validityPeriodDuration)
      );
    }
    return new Date(
      endDate.setMonth(endDate.getMonth() + validityPeriodDuration)
    );
  }

  async renew(): Promise<MedicationPrescribedJournalEvent> {
    return this.store
      .createRecord("medication-prescribed-journal-event", {
        occurredAt: new Date(),
        patient: this.patient,
        validityPeriod: this.validityPeriod,
        medications: this.medications,
        otherMedicationsAld: this.otherMedicationsAld,
        otherMedications: this.otherMedications
      })
      .save();
  }

  get pdfUrl() {
    return `/api/v1/medication_prescriptions/${this.id}`;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "medication-prescribed-journal-event": MedicationPrescribedJournalEvent;
  }
}
